import { useEffect, useRef, useState } from "react";
import { api } from "../app/api";
import { Link } from "react-router-dom";

type BenefitIconName = "efficiency" | "owners" | "compliance" | "integration";
type FeatureIconName = "manage" | "quorum" | "portal" | "hybrid" | "mail" | "editor" | "pdf" | "audit" | "attachments";

const benefits: Array<{ icon: BenefitIconName; title: string; text: string }> = [
  {
    icon: "efficiency",
    title: "Weniger manueller Aufwand",
    text: "Automatisieren Sie bis zu 100 % den Versand und die Erfassung von Stimmen. Sparen Sie wertvolle Stunden pro Beschluss."
  },
  {
    icon: "owners",
    title: "Digitale + analoge Eigentümer",
    text: "Ein nahtloser Prozess für alle. Analoge Stimmen werden via QR-Scan einfach in das digitale System überführt."
  },
  {
    icon: "compliance",
    title: "Nachvollziehbarkeit & Rechtssicherheit",
    text: "Lückenlose Protokollierung inklusive Zustellnachweisen für jeden einzelnen Eigentümer."
  },
  {
    icon: "integration",
    title: "Facilioo-Integration",
    text: "Vermeiden Sie Medienbrüche. Alle Daten werden in Echtzeit aus Facilioo geladen und Ergebnisse fließen direkt in Ihr bestehendes Facilioo-Vorgangssystem."
  }
];

const features: Array<{ icon: FeatureIconName; title: string; text: string }> = [
  { icon: "manage", title: "Beschlüsse managen", text: "Anlegen, starten, stoppen und abschließen mit nur einem Klick - volle Kontrolle über den Zeitplan." },
  { icon: "quorum", title: "Quoren & Stimmrechte", text: "Unterstützung für Kopf-Prinzip, Stimmrecht nach MEA oder Einheiten - flexibel konfigurierbar." },
  { icon: "portal", title: "Eigentümer-Portal", text: "Sicherer Login für Eigentümer zur digitalen Stimmabgabe von überall und jedem Endgerät." },
  { icon: "hybrid", title: "Hybride Stimmabgabe", text: "Analoge Stimmen einfach per QR-/Token-Scan digitalisieren - kein manuelles Abtippen mehr." },
  { icon: "mail", title: "Auto-Versand & Erinnerung", text: "Automatischer Versand der Unterlagen und intelligente Erinnerungen für noch offene Stimmen." },
  { icon: "editor", title: "Vorlagen-Editor", text: "Einfache Anpassung von Einladungen, Erinnerungen und Ergebnissen mit Ihrem CI." },
  { icon: "pdf", title: "PDF-Erzeugung", text: "Vollautomatische Generierung von personalisierten Dokumenten und Protokollen." },
  { icon: "audit", title: "Audit-Protokolle", text: "Lückenlose Dokumentation aller Aktionen für maximale Revisionssicherheit." },
  { icon: "attachments", title: "Unterlagen zu Beschlüssen", text: "Fügen Sie Dateien direkt zum Beschluss hinzu und stellen Sie alle relevanten Unterlagen zentral bereit." }
];

const BenefitIcon = ({ name }: { name: BenefitIconName }) => {
  if (name === "efficiency") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3v3M12 18v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M3 12h3M18 12h3M4.9 19.1 7 17M17 7l2.1-2.1" />
        <circle cx="12" cy="12" r="4.5" />
      </svg>
    );
  }
  if (name === "owners") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="8" cy="9" r="3" />
        <circle cx="16.5" cy="8.5" r="2.5" />
        <path d="M3.5 18.5c.8-2.6 2.8-4 5.5-4s4.8 1.4 5.5 4M14.5 18.5c.5-1.8 1.8-3 3.8-3 1 0 2 .3 2.8 1" />
      </svg>
    );
  }
  if (name === "compliance") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3 5 6v5c0 4.6 2.7 7.6 7 10 4.3-2.4 7-5.4 7-10V6l-7-3Z" />
        <path d="m9.5 12.5 2 2 3.5-4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 7h9a3 3 0 0 1 0 6h-1" />
      <path d="M16 17H7a3 3 0 0 1 0-6h1" />
      <path d="M10 9h4v6h-4z" />
    </svg>
  );
};

const FeatureIcon = ({ name }: { name: FeatureIconName }) => {
  if (name === "manage") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 6h16M4 12h16M4 18h10" />
        <path d="m16 16 2 2 3-4" />
      </svg>
    );
  }
  if (name === "quorum") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 19h14" />
        <path d="M7 19V8M12 19V5M17 19v-9" />
        <circle cx="7" cy="8" r="1.5" />
        <circle cx="12" cy="5" r="1.5" />
        <circle cx="17" cy="10" r="1.5" />
      </svg>
    );
  }
  if (name === "portal") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M13 4h6v16h-6" />
        <path d="M11 12h9" />
        <path d="m8 9 3 3-3 3" />
      </svg>
    );
  }
  if (name === "hybrid") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="3.5" width="10" height="17" rx="2" />
        <path d="M8 6.5h2M17 10h3M17 14h3M17 18h3" />
      </svg>
    );
  }
  if (name === "mail") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="m4 8 8 6 8-6" />
        <path d="M19 4v4M17 6h4" />
      </svg>
    );
  }
  if (name === "editor") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 20h16" />
        <path d="m7 15.5 7.8-7.8 2.5 2.5-7.8 7.8L6 19z" />
      </svg>
    );
  }
  if (name === "pdf") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 3h7l4 4v14H7z" />
        <path d="M14 3v5h4" />
        <path d="M9 16h6M9 13h6" />
      </svg>
    );
  }
  if (name === "audit") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3 5 6v5c0 4.6 2.7 7.6 7 10 4.3-2.4 7-5.4 7-10V6l-7-3Z" />
        <path d="m9.5 12.5 2 2 3.5-4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="6" width="9" height="12" rx="2" />
      <path d="M15 9h5M15 13h5" />
      <path d="m14 17 2 2 4-5" />
    </svg>
  );
};

export const LandingPage = () => {
  const currentYear = new Date().getFullYear();
  const [captchaSiteKey, setCaptchaSiteKey] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [targetEmail, setTargetEmail] = useState("demo@derumlaufbeschluss.de");
  const [submitting, setSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<{ type: "idle" | "success" | "error"; message: string }>({
    type: "idle",
    message: ""
  });
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<string | number | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    document.title = "WEG-Verwaltung Digital | Digitale Umlaufbeschlüsse";
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api
      .getDemoRequestConfig()
      .then((config) => {
        if (cancelled) return;
        setCaptchaSiteKey(String(config.captchaSiteKey ?? "").trim());
        setTargetEmail(String(config.targetEmail ?? "demo@derumlaufbeschluss.de").trim() || "demo@derumlaufbeschluss.de");
      })
      .catch(() => {
        if (cancelled) return;
        setCaptchaSiteKey("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!captchaSiteKey || !captchaContainerRef.current || typeof window === "undefined") return;

    type TurnstileApi = {
      render: (container: HTMLElement, options: Record<string, unknown>) => string | number;
      reset: (widgetId?: string | number) => void;
    };
    const win = window as Window & { turnstile?: TurnstileApi };

    const renderWidget = () => {
      if (!captchaContainerRef.current || !win.turnstile?.render) return;
      captchaContainerRef.current.innerHTML = "";
      captchaWidgetIdRef.current = win.turnstile.render(captchaContainerRef.current, {
        sitekey: captchaSiteKey,
        callback: (token: string) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken("")
      });
    };

    if (win.turnstile?.render) {
      renderWidget();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile="1"]');
    if (existingScript) {
      existingScript.addEventListener("load", renderWidget, { once: true });
      return () => existingScript.removeEventListener("load", renderWidget);
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.turnstile = "1";
    script.addEventListener("load", renderWidget, { once: true });
    document.head.appendChild(script);
    return () => script.removeEventListener("load", renderWidget);
  }, [captchaSiteKey]);

  const submitRequest = async (requestType: "demo_access" | "personal_demo") => {
    if (submitting) return;
    if (!formRef.current) return;
    if (!captchaSiteKey) {
      setFormStatus({
        type: "error",
        message: "Captcha ist aktuell nicht verfügbar. Bitte schreiben Sie direkt an demo@derumlaufbeschluss.de."
      });
      return;
    }
    if (!captchaToken) {
      setFormStatus({ type: "error", message: "Bitte bestätigen Sie zuerst das Captcha." });
      return;
    }

    const data = new FormData(formRef.current);
    const fullName = String(data.get("fullName") ?? "").trim();
    const company = String(data.get("company") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const phone = String(data.get("phone") ?? "").trim();
    const managedUnits = String(data.get("managedUnits") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();

    if (!fullName || !company || !email || !phone) {
      setFormStatus({ type: "error", message: "Bitte füllen Sie Name, Firma, E-Mail und Telefonnummer aus." });
      return;
    }

    setSubmitting(true);
    setFormStatus({ type: "idle", message: "" });
    try {
      await api.submitDemoRequest({
        fullName,
        company,
        email,
        phone,
        managedUnits,
        message,
        requestType,
        captchaToken
      });
      setFormStatus({ type: "success", message: "Vielen Dank. Ihre Anfrage wurde erfolgreich versendet." });
      formRef.current.reset();
      setCaptchaToken("");
      const win = window as Window & { turnstile?: { reset: (widgetId?: string | number) => void } };
      if (win.turnstile?.reset) win.turnstile.reset(captchaWidgetIdRef.current ?? undefined);
    } catch (error) {
      setFormStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Anfrage konnte nicht versendet werden."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-page-v2">
      <header className="lpv2-nav-wrap">
        <div className="lpv2-nav">
          <div className="lpv2-brand">
            <img src="/header-logo.png" alt="Der Umlaufbeschluss" />
          </div>
          <nav>
            <a href="#vorteile">Vorteile</a>
            <a href="#funktionen">Funktionen</a>
            <a href="#integration">Integrationen</a>
            <a href="#preise">Preise</a>
          </nav>
          <a href="#demo" className="lpv2-btn lpv2-btn-dark lpv2-nav-cta">
            Demo anfragen
          </a>
        </div>
      </header>

      <main>
        <section className="lpv2-hero">
          <div className="lpv2-hero-copy">
            <h1>Digitale Umlaufbeschlüsse für die WEG-Verwaltung.</h1>
            <p>
              Einfach erstellen, sicher abstimmen, automatisiert versenden und nahtlos mit Facilioo verbinden.
              <strong>
                {" "}Für Umlaufbeschlüsse, die endlich Freude machen – selbst in großen WEGs schnell, einfach und effizient
                umgesetzt.
              </strong>
            </p>
            <div className="lpv2-actions">
              <a href="#demo" className="lpv2-btn lpv2-btn-dark">
                Demozugang anfragen
              </a>
              <a href="#demo" className="lpv2-btn lpv2-btn-light">
                Live-Demo vereinbaren
              </a>
            </div>
          </div>

          <div className="lpv2-hero-visual">
            <div className="lpv2-dashboard">
              <div className="lpv2-dashboard-head">
                <strong>Umlaufbeschlüsse</strong>
                <button>Neuer Umlaufbeschluss</button>
              </div>
              <div className="lpv2-dashboard-toolbar">
                <span>Suche</span>
                <span>Alle Status</span>
                <span>Alle Ergebnisse</span>
              </div>
              <div className="lpv2-dashboard-table">
                <div className="lpv2-dashboard-row lpv2-dashboard-row-head">
                  <span>Beschluss-ID</span>
                  <span>Objekt</span>
                  <span>Titel</span>
                  <span>Status</span>
                  <span>Rücklauf</span>
                  <span>Ergebnis</span>
                </div>
                <div className="lpv2-dashboard-row">
                  <span>B-2026-D001</span>
                  <span>WEG Rheinufer</span>
                  <span>Erneuerung Treppenhausbeleuchtung</span>
                  <span className="ok">läuft</span>
                  <span>83.50 %</span>
                  <span className="result-none">-</span>
                </div>
                <div className="lpv2-dashboard-row">
                  <span>B-2026-A001</span>
                  <span>WEG Sonnenblick</span>
                  <span>Sanierung Kellerdecke</span>
                  <span className="stop">beendet</span>
                  <span>100.00 %</span>
                  <span className="result-badge result-no">Abgelehnt</span>
                </div>
                <div className="lpv2-dashboard-row">
                  <span>B-2026-E001</span>
                  <span>WEG Gutenberg</span>
                  <span>Anpassung Hausgeldvorauszahlung</span>
                  <span className="stop">beendet</span>
                  <span>96.20 %</span>
                  <span className="result-badge result-yes">Angenommen</span>
                </div>
                <div className="lpv2-dashboard-row">
                  <span>B-2026-F004</span>
                  <span>WEG Lindenhof</span>
                  <span>Austausch Briefkastenanlage</span>
                  <span className="ok">läuft</span>
                  <span>61.80 %</span>
                  <span className="result-none">-</span>
                </div>
                <div className="lpv2-dashboard-row">
                  <span>B-2026-G007</span>
                  <span>WEG Parkresidenz</span>
                  <span>Einführung digitale Zutrittskontrolle</span>
                  <span className="stop">beendet</span>
                  <span>98.70 %</span>
                  <span className="result-badge result-yes">Angenommen</span>
                </div>
                <div className="lpv2-dashboard-row">
                  <span>B-2026-H002</span>
                  <span>WEG Rosenquartier</span>
                  <span>Neugestaltung Innenhof</span>
                  <span className="stop">beendet</span>
                  <span>94.10 %</span>
                  <span className="result-badge result-no">Abgelehnt</span>
                </div>
              </div>
            </div>
            <div className="lpv2-phone">
              <div className="lpv2-notch" />
              <div className="lpv2-phone-screen">
                <div className="lpv2-phone-headline">Digitale Abstimmung</div>
                <div className="lpv2-phone-title-row">
                  <h4>Beratung und Genehmigung der Jahresabrechnung 2025 samt Fälligstellung</h4>
                </div>
                <div className="lpv2-phone-meta">
                  <strong>B-2026-011</strong>
                  <span>läuft</span>
                </div>

                <div className="lpv2-phone-info">
                  <small>Objekt</small>
                  <strong>9994 | WEG Elsa-Brändström-Straße 29-37</strong>
                </div>
                <div className="lpv2-phone-info">
                  <small>Eigentümer</small>
                  <strong>Herrn Max Mustermann</strong>
                </div>
                <div className="lpv2-phone-info">
                  <small>Abstimmungsende</small>
                  <strong>30.04.2026</strong>
                </div>
                <div className="lpv2-phone-info">
                  <small>Quorum</small>
                  <strong>Allstimmig</strong>
                </div>

                <div className="lpv2-phone-panel">
                  <h5>Stimmanteile: Miteigentumsanteile (MEA)</h5>
                  <p>Die Stimmanteile ergibt sich aus den stimmberechtigten Einheiten und deren MEA.</p>
                  <div className="lpv2-phone-metrics">
                    <div className="lpv2-phone-info lpv2-phone-info-compact">
                      <small>Ihre Stimmrecht</small>
                      <strong>152,36</strong>
                    </div>
                    <div className="lpv2-phone-info lpv2-phone-info-compact">
                      <small>Gesamtanateile</small>
                      <strong>1000</strong>
                    </div>
                    <div className="lpv2-phone-info lpv2-phone-info-compact">
                      <small>Ihre Stimmmacht</small>
                      <strong>152,36/1000</strong>
                    </div>
                  </div>
                </div>

                <div className="lpv2-phone-panel">
                  <h5>Beratung und Genehmigung der Jahresabrechnung 2025 samt Fälligstellung</h5>
                  <p>
                    Die vom Verwalter vorgelegte Jahresabrechnung wird beschlossen. Die Abrechnungsspitzen der
                    Einzelabrechnungen sind damit zur Zahlung fällig.
                  </p>
                  <p>
                    Eigentümer mit Einzugsermächtigung zahlen automatisch, sonst ist ein Ausgleich bis zum Monatsende
                    durchzuführen.
                  </p>
                </div>

                <div className="lpv2-phone-panel">
                  <h5>Ihre Stimmauswahl</h5>
                  <div className="lpv2-phone-votes">
                    <button type="button" className="yes">
                      Ja
                    </button>
                    <button type="button" className="no">
                      Nein
                    </button>
                    <button type="button" className="neutral">
                      <span className="lpv2-vote-label-full">Enthaltung</span>
                      <span className="lpv2-vote-label-short">Enth.</span>
                    </button>
                  </div>
                  <div className="lpv2-phone-note">Kommentar (optional)</div>
                  <div className="lpv2-phone-textbox" />
                  <button type="button" className="lpv2-phone-submit">
                    Ja - Stimme verbindlich abgeben
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="vorteile" className="lpv2-section lpv2-surface-2 lpv2-net-bg lpv2-net-left">
          <div className="lpv2-container">
            <h2>Effizienzsteigerung in der Verwaltung</h2>
            <div className="lpv2-benefit-grid">
              {benefits.map((item) => (
                <article key={item.title}>
                  <div className="lpv2-icon" aria-hidden="true">
                    <BenefitIcon name={item.icon} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="funktionen" className="lpv2-section lpv2-surface-1 lpv2-net-bg lpv2-net-right">
          <div className="lpv2-container">
            <div className="lpv2-headline-center">
              <h2>Umfassende Funktionen</h2>
              <p>Alles, was Sie für eine moderne, rechtssichere Beschlussfassung benötigen.</p>
            </div>

              <div className="lpv2-feature-grid">
                {features.map((feature) => (
                  <article key={feature.title}>
                    <div className="lpv2-feature-icon" aria-hidden="true">
                      <FeatureIcon name={feature.icon} />
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.text}</p>
                  </article>
                ))}
              </div>
          </div>
        </section>

        <section id="integration" className="lpv2-integration lpv2-net-bg-dark lpv2-net-left">
          <div className="lpv2-container lpv2-integration-grid">
            <div>
              <span className="lpv2-overline lpv2-overline-dark">Echtzeit Syncronisation</span>
              <h2>Facilioo-Integration ohne Kompromisse</h2>
              <ul>
                    <li>Objektbasierte Prüfung: Beschlüsse werden direkt den Vorgängen in Facilioo zugeordnet.</li>
                    <li>Dokumententransfer: Alle Dokumente sind sofort Bidirektional im Facilioo-Vorgang und umgekehrt in der Beschluss-APP verfügbar.</li>
                    <li>Facilioo-Verlauf: Ergebnisse und Statusupdates fließen, auf Wunsch, automatisch in den Vorgangsverlauf und lösen Meldungen für den Objektbetreuer aus.</li>
              </ul>
            </div>
            <div className="lpv2-eval-mock">
              <div className="lpv2-eval-top">
                <h4>Beschlussauswertung</h4>
                <button type="button">Logdatei öffnen</button>
              </div>
              <div className="lpv2-eval-summary">
                <div className="lpv2-eval-chart" aria-hidden="true" />
                <div className="lpv2-eval-stats">
                  <div>
                    <span>Abgegebene Stimmen</span>
                    <strong>16</strong>
                  </div>
                  <div>
                    <span>Gesamte Stimmzahl</span>
                    <strong>16</strong>
                  </div>
                  <div>
                    <span>Quorum erfüllt</span>
                    <strong>Ja</strong>
                  </div>
                  <div>
                    <span>Endergebnis</span>
                    <strong>Angenommen</strong>
                  </div>
                </div>
              </div>
              <div className="lpv2-eval-table">
                <div className="lpv2-eval-row lpv2-eval-head">
                  <span>Einheit</span>
                  <span>Eigentümer</span>
                  <span>Stimmverhalten</span>
                </div>
                <div className="lpv2-eval-row">
                  <span>0001</span>
                  <span>Miriam Peters</span>
                  <span className="badge neutral">Enthaltung</span>
                </div>
                <div className="lpv2-eval-row">
                  <span>0002</span>
                  <span>Sophie Schäfer</span>
                  <span className="badge yes">Ja</span>
                </div>
                <div className="lpv2-eval-row">
                  <span>0005</span>
                  <span>Katja Seidel</span>
                  <span className="badge no">Nein</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="demo" className="lpv2-section lpv2-surface-2 lpv2-net-bg lpv2-net-right">
          <div className="lpv2-container lpv2-demo-grid">
            <aside className="lpv2-demo-aside">
              <h2>Für wen wir da sind.</h2>
              <p>
                  Speziell aus der Praxis entwickelt für moderne WEG- & Hausverwaltungen, die sowohl digitale als auch
                  analoge Eigentümer professionell bedienen möchten.
              </p>
              <div className="lpv2-pricing-note">
                <h3>Faire Preise für echte Herausforderungen in der Hausverwaltung</h3>
                <p>
                  Wir wissen, wie unterschiedlich die wirtschaftlichen Rahmenbedingungen in der Hausverwaltung sein
                  können. Genau deshalb entwickeln wir derzeit ein Pricing-Modell, das sich nicht nur an Funktionen
                  orientiert, sondern an der Realität Ihres Geschäfts. Unser Ziel ist es, eine Lösung zu schaffen, die
                  fair, nachvollziehbar und an Ihre Einnahmesituation angepasst ist.{" "}
                  <strong>
                    Wir nehmen uns bewusst die Zeit, um ein Modell zu gestalten, das Sie langfristig unterstützt und
                    nicht belastet.
                  </strong>
                </p>
              </div>
            </aside>

            <div id="preise" className="lpv2-demo-form-wrap">
              <h3>Demozugang anfragen</h3>
              <form
                ref={formRef}
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitRequest("demo_access");
                }}
              >
                <div className="lpv2-form-grid">
                  <label>Vollständiger Name<input name="fullName" placeholder="Vorname Nachname" required /></label>
                  <label>Firma / Verwaltung<input name="company" placeholder="Ihre Verwaltung" required /></label>
                  <label>E-Mail Adresse<input name="email" type="email" placeholder="name@verwaltung.de" required /></label>
                  <label>Telefon<input name="phone" placeholder="+49 ..." required /></label>
                  <label className="span2">Anzahl verwalteter Einheiten (Optional)
                    <select name="managedUnits" defaultValue="Bis 500 Einheiten">
                      <option>Bis 500 Einheiten</option>
                      <option>500 - 2.000 Einheiten</option>
                      <option>Über 2.000 Einheiten</option>
                    </select>
                  </label>
                  <label className="span2">Ihre Nachricht (Optional)<textarea name="message" rows={4} placeholder="Wie können wir Ihnen helfen?" /></label>
                </div>
                <div className="lpv2-captcha-wrap">
                  {captchaSiteKey ? (
                    <div ref={captchaContainerRef} />
                  ) : (
                    <p className="lpv2-captcha-missing">Captcha ist aktuell nicht konfiguriert. Direkter Kontakt: {targetEmail}</p>
                  )}
                </div>
                <div className="lpv2-demo-actions">
                  <button type="submit" className="primary" disabled={submitting}>
                    {submitting ? "Wird gesendet..." : "Demozugang anfragen"}
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => {
                      void submitRequest("personal_demo");
                    }}
                  >
                    Termin für persönliche Produktdemo
                  </button>
                </div>
                {formStatus.type !== "idle" ? (
                  <p className={`lpv2-demo-form-status ${formStatus.type}`}>{formStatus.message}</p>
                ) : null}
                <p>Wir melden uns kurzfristig mit Testzugang und kurzer Einführung.</p>
              </form>
            </div>
          </div>
        </section>
      </main>
      <footer className="lpv2-footer">
        <div className="lpv2-container lpv2-footer-inner">
          <nav className="lpv2-footer-links" aria-label="Rechtliche Links">
            <Link to="/impressum">Impressum</Link>
            <Link to="/datenschutz">Datenschutz</Link>
          </nav>
          <span className="lpv2-footer-copy">© {currentYear} Emig Immobilien</span>
        </div>
      </footer>

    </div>
  );
};











